import type { AxiosError } from 'axios'

import type { ErrorContext, ErrorHandlerResult } from '../ports/ErrorHandlerPort'
import { ErrorHandlerRegistry } from './ErrorHandlerRegistry'

/**
 * Main service that orchestrates error handling using the registry
 * This is the main adapter that implements the error handling port
 */
export class HttpErrorHandlerService {
  constructor(private readonly registry: ErrorHandlerRegistry) {}

  /**
   * Handles an HTTP error using registered strategies
   * @param error - The axios error object
   * @param context - Additional context about the request
   * @returns Promise<ErrorHandlerResult> - Result of error handling
   */
  async handleError(error: AxiosError, context: ErrorContext = {}): Promise<ErrorHandlerResult> {
    const method = error.config?.method?.toUpperCase()
    const enrichedContext: ErrorContext = {
      url: error.config?.url,
      method,
      status: error.response?.status,
      data: this.extractRequestData(error, method),
      ...context
    }

    return await this.registry.handleError(error, enrichedContext)
  }

  /**
   * Extracts request data for POST, PUT, PATCH requests
   * @param error - The axios error object
   * @param method - The HTTP method (uppercase)
   * @returns The request data or undefined
   */
  private extractRequestData(error: AxiosError, method?: string): unknown {
    if (!method || !['POST', 'PUT', 'PATCH'].includes(method)) {
      return undefined
    }

    return error.config?.data
  }

  /**
   * Gets the registry for advanced operations (testing, debugging)
   */
  getRegistry(): ErrorHandlerRegistry {
    return this.registry
  }
}
